import { Component, computed, OnInit } from '@angular/core';
import { APP_CONFIG } from '@core-app/app.config';
import { BlockAbstractComponent } from '@gallaghercp/core-frontend';

type ResponsiveValue<T> = T | { mobile?: T; tablet?: T; desktop?: T };

interface HomeHeroBannerThemeConfig {
    blocks?: {
        homeHeroBanner?: {
            container?: {
                borderRadius?: string;
            };
            contentContainer?: {
                padding?: ResponsiveValue<string>;
            };
            typography?: {
                heading?: {
                    fontSize?: ResponsiveValue<string>;
                    fontWeight?: ResponsiveValue<string>;
                    lineHeight?: ResponsiveValue<string>;
                    letterSpacing?: ResponsiveValue<string>;
                };
                description?: {
                    fontSize?: ResponsiveValue<string>;
                    fontWeight?: ResponsiveValue<string>;
                    lineHeight?: ResponsiveValue<string>;
                    letterSpacing?: ResponsiveValue<string>;
                };
            };
        };
    };
    theme?: {
        homeHeroBannerHeight?: unknown;
    };
}

@Component({
    selector: 'core-block-home-hero-banner-v2',
    templateUrl: './block-home-hero-banner-v2.component.html',
    styleUrl: './block-home-hero-banner-v2.component.scss',
    standalone: false,
})
export class BlockHomeHeroBannerV2Component extends BlockAbstractComponent implements OnInit {
    hasHeading!: boolean;

    homeHeroConfig = computed(() => (APP_CONFIG as unknown as HomeHeroBannerThemeConfig).blocks?.homeHeroBanner);

    homeHeroStyles = computed(() => {
        const contentContainerPadding = this.createResponsiveCssVars(
            '--hero-content-container-padding',
            this.homeHeroConfig()?.contentContainer?.padding,
        );

        const headingTypography = this.homeHeroConfig()?.typography?.heading;
        const headingFontSizes = this.createResponsiveCssVars('--hero-heading-font-size', headingTypography?.fontSize);
        const headingFontWeight = this.createResponsiveCssVars(
            '--hero-heading-font-weight',
            headingTypography?.fontWeight,
        );
        const headingLineHeight = this.createResponsiveCssVars(
            '--hero-heading-line-height',
            headingTypography?.lineHeight,
        );
        const headingLetterSpacing = this.createResponsiveCssVars(
            '--hero-heading-letter-spacing',
            headingTypography?.letterSpacing,
        );

        const descriptionTypography = this.homeHeroConfig()?.typography?.description;
        const descriptionFontSizes = this.createResponsiveCssVars(
            '--hero-description-font-size',
            descriptionTypography?.fontSize,
        );
        const descriptionFontWeight = this.createResponsiveCssVars(
            '--hero-description-font-weight',
            descriptionTypography?.fontWeight,
        );
        const descriptionLineHeight = this.createResponsiveCssVars(
            '--hero-description-line-height',
            descriptionTypography?.lineHeight,
        );
        const descriptionLetterSpacing = this.createResponsiveCssVars(
            '--hero-description-letter-spacing',
            descriptionTypography?.letterSpacing,
        );

        return {
            ...contentContainerPadding,

            '--hero-border-radius': this.homeHeroConfig()?.container?.borderRadius,
            '--hero-min-height': this.homeHeroBannerHeight,

            ...headingFontSizes,
            ...headingFontWeight,
            ...headingLineHeight,
            ...headingLetterSpacing,

            ...descriptionFontSizes,
            ...descriptionFontWeight,
            ...descriptionLineHeight,
            ...descriptionLetterSpacing,
        };
    });

    override ngOnInit() {
        super.ngOnInit();

        this.hasHeading = this.block.data.heading || this.block.data.headingTop || this.block.data.headingBottom;
    }

    getBackground() {
        const layers = [];

        if (this.block.data.bgFillColor === 'gradient' && this.block.data.bgLinearGradient) {
            layers.push(`linear-gradient(${this.block.data.bgLinearGradient})`);
        }

        if (this.block.data.useBgImage && this.block.data.image) {
            layers.push(`url(${this.block.data.image})`);
        }

        return layers.length > 0 ? layers.join(', ') : 'none';
    }

    getBackgroundColor() {
        switch (this.block.data.bgFillColor) {
            case 'white':
                return 'white';

            case 'primary':
                return 'var(--color-primary)';

            case 'secondary':
                return 'var(--color-secondary)';

            case 'secondary-2':
                return 'var(--color-secondary-2)';

            case 'gradient':
                return 'transparent';

            default:
                return this.block.data.bgFillColor || 'var(--color-primary)';
        }
    }

    get homeHeroBannerHeight(): string | null {
        const height = (APP_CONFIG as unknown as HomeHeroBannerThemeConfig).theme?.homeHeroBannerHeight;

        return typeof height === 'string' && height.trim() ? height : null;
    }

    private getResponsiveValue(value?: string | ResponsiveValue<string>): {
        mobile?: string;
        tablet?: string;
        desktop?: string;
    } {
        if (typeof value === 'string')
            return {
                mobile: value,
                tablet: value,
                desktop: value,
            };

        return value ?? {};
    }

    private createResponsiveCssVars(prefix: string, values?: ResponsiveValue<string>) {
        const responsiveValue = this.getResponsiveValue(values);

        return {
            [`${prefix}--mobile`]: responsiveValue.mobile,
            [`${prefix}--tablet`]: responsiveValue.tablet,
            [`${prefix}--desktop`]: responsiveValue.desktop,
        };
    }
}
